import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Users, UserPlus, FileText, Key, Clock, History, Mail } from 'lucide-react';

/**
 * Admin Hub — the landing grid that links into all admin-management sub-pages.
 */
export default function AdminHubSection({
  language,
  setActiveSection,
  fetchSupervisorRecords,
  fetchAllAdmins,
  fetchPendingAdmins,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-hub-section"
    >
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <Users className="h-6 w-6 text-emerald-400" />
        {language === 'ar' ? 'إدارة حسابات المشرفين' : 'Admin Accounts Management'}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Add Supervisors/Employees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ y: -5 }}
          onClick={() => setActiveSection('add-admin-id')}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-add-admin-id"
        >
          <div className="w-14 h-14 bg-[#d4af37]/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-[#d4af37]/20">
            <UserPlus className="h-8 w-8 text-[#d4af37]" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'إضافة المشرفين والموظفين' : 'Add Supervisors & Employees'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'عبر بطاقة الهوية الإماراتية' : 'Via Emirates ID'}
          </p>
        </motion.div>

        {/* Supervisor Records */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          whileHover={{ y: -5 }}
          onClick={() => {
            setActiveSection('supervisor-records');
            fetchSupervisorRecords();
          }}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-supervisor-records"
        >
          <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-amber-500/20">
            <FileText className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'سجلات المشرفين والموظفين' : 'Supervisors & Staff Records'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'بيانات نماذج الإضافة' : 'Add-form data entries'}
          </p>
        </motion.div>

        {/* Supervisor Accounts (login users) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -5 }}
          onClick={() => {
            setActiveSection('all-admins');
            fetchAllAdmins();
          }}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-view-all-admins"
        >
          <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/20">
            <Users className="h-8 w-8 text-emerald-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'حسابات المشرفين والموظفين' : 'Supervisors & Staff Accounts'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'إيقاف، حذف، إعادة تعيين' : 'Stop, Delete, Reset'}
          </p>
        </motion.div>

        {/* Create User Account */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          whileHover={{ y: -5 }}
          onClick={() => setActiveSection('create-admin')}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-create-admin-account"
        >
          <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-blue-500/20">
            <Key className="h-8 w-8 text-blue-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'إنشاء حساب يوزر' : 'Create User Account'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'للمشرفين المُدرجين' : 'For registered supervisors'}
          </p>
        </motion.div>

        {/* Pending Admins */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -5 }}
          onClick={() => {
            setActiveSection('pending-admins');
            fetchPendingAdmins();
          }}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-pending-admins"
        >
          <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-amber-500/20">
            <Clock className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'متابعة الجديد' : 'Follow Up New'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'بانتظار التفعيل' : 'Pending Activation'}
          </p>
        </motion.div>

        {/* Activity Log (placeholder) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          whileHover={{ y: -5 }}
          onClick={() => toast.info(language === 'ar' ? 'سجل النشاطات - قريباً' : 'Activity Log - Coming Soon')}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer text-center relative opacity-60"
          data-testid="btn-activity-log"
        >
          <span className="absolute top-3 left-3 bg-white/5 text-[#71717A] text-xs px-2 py-1 rounded-full">
            {language === 'ar' ? 'قريباً' : 'Soon'}
          </span>
          <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 border border-cyan-500/20">
            <History className="h-8 w-8 text-cyan-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'سجل النشاطات' : 'Activity Log'}
          </h3>
        </motion.div>

        {/* Email Deliverability Diagnostics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -5 }}
          onClick={() => setActiveSection('email-diagnostics')}
          className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer text-center"
          data-testid="btn-email-diagnostics"
        >
          <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-cyan-500/20">
            <Mail className="h-8 w-8 text-cyan-400" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {language === 'ar' ? 'تشخيص توصيل البريد' : 'Email Deliverability'}
          </h3>
          <p className="text-xs text-[#71717A]">
            {language === 'ar' ? 'فحص Suppression — حل مشكلة عدم وصول الإيميل' : 'Fix silent SendGrid drops'}
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}
